import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Award, Lightbulb } from 'lucide-react';
import { mockData } from '../data/mock';

const Home = () => {
   return (//bg-gradient-to-br from-primary to-primary-dark
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary to-primary-dark pt-28 relative overflow-hidden" style={{ paddingTop: '6.5rem' }}>
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-text mb-6 animate-fade-in-up">
              Crafting Timeless
              <span className="block text-black"> Digital Experiences</span>
            </h1>
            <p className="text-xl text-gray-800 mb-8 leading-relaxed animate-fade-in-up">
              We build brands that connect visually, speak strategically, and perform digitally.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up">
              <Link to="/portfolio" className="btn-primary text-lg px-8 py-4">
                View Our Work <ArrowRight size={20} />
              </Link>
              <Link to="/contact" className="btn-secondaryss  text-lg px-8 py-4" >
                Start Your Project
              </Link>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-black/5 rounded-full blur-3xl"></div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-center">
            {mockData.stats.map((stat, index) => (
              <div
                key={index}
                className={`space-y-2 ${
                  // ✅ center the last item only on small screens
                  index === mockData.stats.length - 1 ? 'col-span-full mx-auto md:col-span-1' : ''
                }`}
              >
                <div className="text-4xl font-bold text-black">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Services Preview */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Our Creative Services</h2>
            <p className="text-xl colorm max-w-2xl mx-auto">
              From concept to execution, we deliver comprehensive design solutions that elevate your brand
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.services.slice(0, 3).map((service, index) => (
              <div key={index} className="card text-center group">
                <div className="w-16 h-16 border border-white bg-black rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <service.icon size={24} className="text-white" />
                </div>
                <h3 className="subsection-headers mb-4">{service.title}</h3>
                <p className="text-gray-200 mb-6 leading-relaxed">{service.description}</p>
                <Link to="/services" className="btn-secondarys">Learn More</Link>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary">
              View All Services <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Featured Projects</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Discover how we've helped brands transform their digital presence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.portfolio.slice(0, 3).map((project, index) => (
              <div key={index} className="card-white group cursor-pointer">
                <div className="aspect-video bg-black rounded-lg mb-4 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-600">{project.category}</span>
                  <h3 className="font-semibold text-lg">{project.title}</h3>
                  <p className="text-gray-600 text-sm">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/portfolio" className="btn-primary">
              View Full Portfolio <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">What Our Clients Say</h2>
            <p className="text-xl colorm max-w-2xl mx-auto">
              Don't just take our word for it - hear from the brands we've helped succeed
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.testimonials.slice(0, 3).map((testimonial, index) => (
              <div key={index} className="card ">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-200 mb-6 italic leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="bg-black w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.position}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white text-black">
        <div className="container text-center">
          <h2 className="section-header mb-4 text-black">Ready to Transform Your Brand?</h2>
          <p className="text-xl text-gray-500 mb-8 max-w-2xl mx-auto">
            Let's collaborate to create something extraordinary that sets your brand apart from the competition
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-black border-2 border-black text-white px-8 py-4 rounded-lg font-semibold hover:text-black hover:bg-gray-100 transition-colors">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="border-2 border-black text-gray-600 px-8 py-4 rounded-lg font-semibold hover:bg-black hover:text-white transition-colors">
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;